import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { FilterIcon } from '@/components/icons'
import { useWarmupRecommendation } from '@/hooks/useWarmupRecommendation'
import { campaignBudgetForSubmission } from '@/utils/advantage-leads'
import {
    EVENT_CHOICE_AUTOMATIC,
    WARMUP_OFFERED_STEP,
    WARMUP_STEP,
    eventLabel,
    resolveLaunchEvent,
    warmupFeaturesOffered,
} from '@/utils/pixel-warmup'
import {
    LEAD_EVENT_TYPES,
    SALES_EVENT_TYPES,
    DEFAULT_EVENT_TYPES,
    AUDIENCE_CREATION_SETTING_NOTE,
} from '@/utils/ad-campaign-constants'

interface EventTypeSettingsProps {
    stage: string
    audience: string
    getStepContentData: (step: string) => any
    /** Show the stored value without letting it change (published ad sets in edit mode). */
    readOnly?: boolean
}

const EventTypeSettings: React.FC<EventTypeSettingsProps> = ({
    stage,
    audience,
    getStepContentData,
    readOnly = false,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const campaignObjectiveData = getStepContentData('campaignObjective') || {}
    const selectedObjective = campaignObjectiveData.selectedObjective?.toLowerCase()
    const prefetched = getStepContentData('prefetchedData') || {}
    // Editing a campaign or adding audiences to one: the event is picked by the user, never chosen automatically
    const editsExistingCampaign =
        prefetched.isEditMode === true || prefetched.isEditAdSetMode === true || prefetched.isAddAudience === true

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedEventType = audienceSettings?.custom_event_type
    const automaticPicked = audienceSettings?.event_choice === EVENT_CHOICE_AUTOMATIC

    // Determine default value and available options based on objective
    const defaultEventType = DEFAULT_EVENT_TYPES[selectedObjective as keyof typeof DEFAULT_EVENT_TYPES]
    const eventOptions = selectedObjective === 'leads' ? LEAD_EVENT_TYPES : SALES_EVENT_TYPES

    const [selectedEventType, setSelectedEventType] = useState<string>(storedEventType || defaultEventType || '')

    // "Automatic" is offered only where the licence has the feature, and not when an existing campaign is edited
    const { featureAvailable } = useWarmupRecommendation({
        objective: selectedObjective,
        enabled: !readOnly && !editsExistingCampaign,
    })

    // Initialize from stored value or set default
    useEffect(() => {
        if (readOnly) return
        if (storedEventType) {
            setSelectedEventType(storedEventType)
        } else if (defaultEventType) {
            setSelectedEventType(defaultEventType)
            updateAudienceSetting(stage, audience, 'custom_event_type', defaultEventType)
        }
    }, [storedEventType, defaultEventType, stage, audience, updateAudienceSetting, readOnly])

    // Only render for leads and sales campaigns
    if (!selectedObjective || !['leads', 'sales'].includes(selectedObjective)) {
        return null
    }

    const handleEventTypeChange = (eventType: string) => {
        if (eventType === EVENT_CHOICE_AUTOMATIC) {
            updateAudienceSetting(stage, audience, 'event_choice', EVENT_CHOICE_AUTOMATIC)
            return
        }
        setSelectedEventType(eventType)
        updateAudienceSetting(stage, audience, 'custom_event_type', eventType)
        if (automaticPicked) updateAudienceSetting(stage, audience, 'event_choice', null)
    }

    const showAutomatic = featureAvailable && !readOnly && !editsExistingCampaign
    // What launches follows the launch gate: before the budget step has reported, this step's own read stands in for it
    const reported = getStepContentData(WARMUP_OFFERED_STEP)?.feature
    const launch = resolveLaunchEvent({
        eventChoice: automaticPicked ? EVENT_CHOICE_AUTOMATIC : null,
        stored: storedEventType,
        objective: selectedObjective,
        offered:
            showAutomatic &&
            warmupFeaturesOffered({
                objective: selectedObjective,
                offeredStep: { feature: reported ?? featureAvailable },
                campaignBudget: !!campaignBudgetForSubmission(getStepContentData('budgetSchedule')),
            }),
        warmup: getStepContentData(WARMUP_STEP) || {},
    })
    const automaticShown = automaticPicked && launch.automatic
    // A pick that no longer counts shows the event that launches instead
    const shownEvent = automaticPicked && !launch.automatic ? launch.event || selectedEventType : selectedEventType
    const automaticTargetLabel = eventLabel(launch.event)

    const selectedOption = eventOptions.filter(
        (x) => x.value === (readOnly ? storedEventType || defaultEventType : shownEvent),
    )[0]
    const selectValue = automaticShown ? EVENT_CHOICE_AUTOMATIC : shownEvent

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <FilterIcon className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Event Type</span>
                <Tooltip>
                    <TooltipTrigger>
                        <Info className="tw:w-4 tw:h-4 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent className="tw:max-w-80" showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400">
                            Select the specific event type for your {selectedObjective} campaign.
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            {readOnly ? (
                <div className="tw:flex tw:flex-col tw:gap-1 tw:p-3 tw:pt-0" data-testid="event-type-read-only">
                    <span className="tw:font-medium">{selectedOption?.label ?? storedEventType}</span>
                    <span className="tw:text-sm tw:text-gray-200">{AUDIENCE_CREATION_SETTING_NOTE}</span>
                </div>
            ) : (
                <div className="tw:p-3 tw:pt-0">
                    <Select value={selectValue} onValueChange={handleEventTypeChange}>
                        <SelectTrigger className="tw:w-full">
                            <SelectValue placeholder={`Select event type for ${selectedObjective} campaign`}>
                                {selectValue === EVENT_CHOICE_AUTOMATIC ? 'Automatic' : selectedOption?.label}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="tw:bg-primary tw:border-slate-700">
                            {showAutomatic && (
                                <SelectItem
                                    key={EVENT_CHOICE_AUTOMATIC}
                                    value={EVENT_CHOICE_AUTOMATIC}
                                    className="tw:text-white tw:hover:bg-slate-700"
                                    handleDelete={() => {}}
                                >
                                    <div className="tw:flex tw:flex-col tw:gap-0.5">
                                        <span className="tw:font-medium">Automatic</span>
                                        <span className="tw:text-sm tw:text-gray-200">
                                            Turba uses the target event of the campaign
                                        </span>
                                    </div>
                                </SelectItem>
                            )}
                            {eventOptions.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                    className="tw:text-white tw:hover:bg-slate-700"
                                    handleDelete={() => {}}
                                >
                                    <div className="tw:flex tw:flex-col tw:gap-0.5">
                                        <span className="tw:font-medium">{option.label}</span>
                                        <span className="tw:text-sm tw:text-gray-200">{option.description}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectValue === EVENT_CHOICE_AUTOMATIC && (
                        <p className="tw:mt-2 tw:text-sm tw:text-gray-200" data-testid="event-type-automatic-note">
                            This ad set optimises for the target event of the campaign ({automaticTargetLabel}) when it
                            is published. Pick an event yourself to keep full control.
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export default EventTypeSettings
